import { createContext, useContext, useEffect, useRef, useState } from 'react';

interface MusicContextType {
  isMuted: boolean;
  isPlaying: boolean;
  toggleMute: () => void;
  play: () => void;
  pause: () => void;
  switchMusic: (newSrc: string) => void;
  currentSrc: string;
}

const MusicContext = createContext<MusicContextType | undefined>(undefined);

interface MusicProviderProps {
  children: React.ReactNode;
  src: string;
  volume?: number;
  autoPlay?: boolean;
  loop?: boolean;
}

export const MusicProvider = ({
  children,
  src,
  volume = 0.3,
  autoPlay = true,
  loop = true,
}: MusicProviderProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const attemptedAutoPlayRef = useRef(false);
  const volumeRef = useRef(volume);

  useEffect(() => {
    // Create audio element
    const audio = new Audio(currentSrc);
    audio.volume = volumeRef.current;
    audio.loop = loop;
    audioRef.current = audio;

    // Auto play on mount or when switching music
    if (autoPlay || isPlaying) {
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          attemptedAutoPlayRef.current = true;
        })
        .catch(() => {
          // Audio autoplay prevented by browser
        });
    }

    // Auto-play on first user interaction if autoplay was blocked
    const handleFirstInteraction = () => {
      if (!attemptedAutoPlayRef.current && autoPlay) {
        audio
          .play()
          .then(() => {
            setIsPlaying(true);
            attemptedAutoPlayRef.current = true;
            // Remove listeners after successful play
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('touchstart', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
          })
          .catch(() => {
            // Failed to play audio
          });
      }
    };

    // Add event listeners for first user interaction
    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction);
    document.addEventListener('keydown', handleFirstInteraction);

    // Cleanup
    return () => {
      audio.pause();
      audio.currentTime = 0;
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
  }, [currentSrc, loop, autoPlay, isPlaying]);

  // Update volume ref
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  // Update mute state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volumeRef.current;
    }
  }, [isMuted]);

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!isPlaying) {
      // First interaction - start playing
      audio.play().then(() => {
        setIsPlaying(true);
        setIsMuted(false);
      });
    } else {
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
    }
  };

  const play = () => {
    audioRef.current?.play().then(() => {
      setIsPlaying(true);
    });
  };

  const pause = () => {
    audioRef.current?.pause();
    setIsPlaying(false);
  };

  const switchMusic = (newSrc: string) => {
    setCurrentSrc(newSrc);
  };

  return (
    <MusicContext.Provider
      value={{ isMuted, isPlaying, toggleMute, play, pause, switchMusic, currentSrc }}
    >
      {children}
    </MusicContext.Provider>
  );
};

export const useMusic = () => {
  const context = useContext(MusicContext);
  if (context === undefined) {
    throw new Error('useMusic must be used within a MusicProvider');
  }
  return context;
};
